<template>
  <div class="prediction-accuracy-widget">
    <div class="widget-header">
      <h6 class="widget-title">PV & Load Prediction Accuracy</h6>
      <button 
        v-if="hasData && accessLevelNumber == 10"
        @click="showAdvanced = !showAdvanced" 
        class="toggle-advanced-btn"
        :class="{ active: showAdvanced }"
      >
        {{ showAdvanced ? 'Less' : 'Advanced' }}
      </button>
    </div>

    <div v-if="!hasData" class="no-data-message">
      <p>No prediction data available</p>
    </div>

    <div v-else class="widget-content">
      <!-- Basic Metrics -->
      <div class="basic-metrics">
        <!-- Load Accuracy -->
        <div class="metric-card">
          <div class="metric-header">
            <span class="metric-icon">⚡</span>
            <span class="metric-name">Load Energy</span>
          </div>
          <div class="metric-comparison">
            <div class="comparison-row">
              <span class="label">Predicted:</span>
              <span class="value">{{ formatKWh(loadPredictedTotal) }} kWh</span>
            </div>
            <div class="comparison-row">
              <span class="label">Actual:</span>
              <span class="value">{{ formatKWh(loadActualTotal) }} kWh</span>
            </div>
            <div class="accuracy-row">
              <span class="label">Accuracy:</span>
              <span 
                class="accuracy-value" 
                :class="getAccuracyClass(loadAccuracy)"
              >
                {{ formatPercent(loadAccuracy) }}
              </span>
              <div class="accuracy-bar-container">
                <div 
                  class="accuracy-bar"
                  :style="{ width: loadAccuracy + '%', backgroundColor: getAccuracyColor(loadAccuracy) }"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- PV Accuracy -->
        <div class="metric-card">
          <div class="metric-header">
            <span class="metric-icon">☀️</span>
            <span class="metric-name">PV Energy</span>
          </div>
          <div class="metric-comparison">
            <div class="comparison-row">
              <span class="label">Predicted:</span>
              <span class="value">{{ formatKWh(pvPredictedTotal) }} kWh</span>
            </div>
            <div class="comparison-row">
              <span class="label">Actual:</span>
              <span class="value">{{ formatKWh(pvActualTotal) }} kWh</span>
            </div>
            <div class="accuracy-row">
              <span class="label">Accuracy:</span>
              <span 
                class="accuracy-value" 
                :class="getAccuracyClass(pvAccuracy)"
              >
                {{ formatPercent(pvAccuracy) }}
              </span>
              <div class="accuracy-bar-container">
                <div 
                  class="accuracy-bar"
                  :style="{ width: pvAccuracy + '%', backgroundColor: getAccuracyColor(pvAccuracy) }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Formula Explanation Section -->
      <div class="formula-section">
        <button 
          @click="showFormula = !showFormula" 
          class="formula-toggle-btn"
          :class="{ expanded: showFormula }"
        >
          <span class="toggle-icon">{{ showFormula ? '▼' : '▶' }}</span>
          How is this calculated?
        </button>
        <transition name="formula-expand">
          <div v-if="showFormula" class="formula-content">
            <div class="formula-box">
              <div class="formula-title">Accuracy Formula</div>
              <div class="formula-expression" v-html="renderLatex('\\text{Accuracy} \\% = \\left(1 - \\frac{|\\text{Predicted} - \\text{Actual}|}{\\max(\\text{Predicted}, \\text{Actual})}\\right) \\times 100', true)"></div>
            </div>
            <p class="formula-description">
              This symmetric formula compares the total predicted energy against actual energy, using the larger value as reference to ensure fair accuracy measurement for both over-predictions and under-predictions.
            </p>
          </div>
        </transition>
      </div>

      <!-- Advanced Metrics -->
      <div v-if="showAdvanced && accessLevelNumber == 10" class="advanced-metrics">
        <div class="advanced-section">
          <h6 class="advanced-title">Load Prediction Metrics</h6>
          <div class="metrics-grid">
            <div class="stat-metric">
              <span class="stat-label">MSE</span>
              <span class="stat-value">
                {{ formatMetric(loadMetrics.mse) }}
                <span v-if="loadMetrics.mseRank" class="stat-rank-dot" :class="getRankClass(loadMetrics.mseRank)"></span>
              </span>
            </div>
            <div class="stat-metric">
              <span class="stat-label">MAE</span>
              <span class="stat-value">
                {{ formatMetric(loadMetrics.mae) }}
                <span v-if="loadMetrics.maeRank" class="stat-rank-dot" :class="getRankClass(loadMetrics.maeRank)"></span>
              </span>
            </div>
            <div class="stat-metric">
              <span class="stat-label">MAPE</span>
              <span class="stat-value">
                {{ formatPercent(loadMetrics.mape) }}
                <span class="stat-rank-dot" :class="getRankClass(loadMetrics.mapeRank)"></span>
              </span>
            </div>
            <div class="stat-metric">
              <span class="stat-label">RMSE</span>
              <span class="stat-value">
                {{ formatMetric(loadMetrics.rmse) }}
                <span v-if="loadMetrics.rmseRank" class="stat-rank-dot" :class="getRankClass(loadMetrics.rmseRank)"></span>
              </span>
            </div>
            <div class="stat-metric">
              <span class="stat-label">NRMSE</span>
              <span class="stat-value">
                {{ formatMetric(loadMetrics.nrmse) }}
                <span class="stat-rank-dot" :class="getRankClass(loadMetrics.nrmseRank)"></span>
              </span>
            </div>
            <div class="stat-metric">
              <span class="stat-label">σ (Sigma)</span>
              <span class="stat-value">
                {{ formatMetric(loadMetrics.sigma) }}
                <span v-if="loadMetrics.sigmaRank" class="stat-rank-dot" :class="getRankClass(loadMetrics.sigmaRank)"></span>
              </span>
            </div>
            <div class="stat-metric">
              <span class="stat-label">CE</span>
              <span class="stat-value">
                {{ formatMetric(loadMetrics.ce) }}
                <span class="stat-rank-dot" :class="getRankClass(loadMetrics.ceRank)"></span>
              </span>
            </div>
            <div class="stat-metric">
              <span class="stat-label">CRPS</span>
              <span class="stat-value">
                {{ formatMetric(loadMetrics.crps) }}
                <span v-if="loadMetrics.crpsRank" class="stat-rank-dot" :class="getRankClass(loadMetrics.crpsRank)"></span>
              </span>
            </div>
          </div>
        </div>

        <div class="advanced-section">
          <h6 class="advanced-title">PV Prediction Metrics</h6>
          <div class="metrics-grid">
            <div class="stat-metric">
              <span class="stat-label">MSE</span>
              <span class="stat-value">
                {{ formatMetric(pvMetrics.mse) }}
                <span v-if="pvMetrics.mseRank" class="stat-rank-dot" :class="getRankClass(pvMetrics.mseRank)"></span>
              </span>
            </div>
            <div class="stat-metric">
              <span class="stat-label">MAE</span>
              <span class="stat-value">
                {{ formatMetric(pvMetrics.mae) }}
                <span v-if="pvMetrics.maeRank" class="stat-rank-dot" :class="getRankClass(pvMetrics.maeRank)"></span>
              </span>
            </div>
            <div class="stat-metric">
              <span class="stat-label">MAPE</span>
              <span class="stat-value">
                {{ formatPercent(pvMetrics.mape) }}
                <span class="stat-rank-dot" :class="getRankClass(pvMetrics.mapeRank)"></span>
              </span>
            </div>
            <div class="stat-metric">
              <span class="stat-label">RMSE</span>
              <span class="stat-value">
                {{ formatMetric(pvMetrics.rmse) }}
                <span v-if="pvMetrics.rmseRank" class="stat-rank-dot" :class="getRankClass(pvMetrics.rmseRank)"></span>
              </span>
            </div>
            <div class="stat-metric">
              <span class="stat-label">NRMSE</span>
              <span class="stat-value">
                {{ formatMetric(pvMetrics.nrmse) }}
                <span class="stat-rank-dot" :class="getRankClass(pvMetrics.nrmseRank)"></span>
              </span>
            </div>
            <div class="stat-metric">
              <span class="stat-label">σ (Sigma)</span>
              <span class="stat-value">
                {{ formatMetric(pvMetrics.sigma) }}
                <span v-if="pvMetrics.sigmaRank" class="stat-rank-dot" :class="getRankClass(pvMetrics.sigmaRank)"></span>
              </span>
            </div>
            <div class="stat-metric">
              <span class="stat-label">CE</span>
              <span class="stat-value">
                {{ formatMetric(pvMetrics.ce) }}
                <span class="stat-rank-dot" :class="getRankClass(pvMetrics.ceRank)"></span>
              </span>
            </div>
            <div class="stat-metric">
              <span class="stat-label">CRPS</span>
              <span class="stat-value">
                {{ formatMetric(pvMetrics.crps) }}
                <span v-if="pvMetrics.crpsRank" class="stat-rank-dot" :class="getRankClass(pvMetrics.crpsRank)"></span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Rank Legend -->
      <div v-if="showAdvanced && accessLevelNumber == 10" class="rank-legend">
        <span class="legend-title">Result Quality Indicator:</span>
        <div class="legend-items">
          <div class="legend-item">
            <span class="legend-dot rank-excellent"></span>
            <span class="legend-label">Excellent</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot rank-good"></span>
            <span class="legend-label">Good</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot rank-fair"></span>
            <span class="legend-label">Fair</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot rank-acceptable"></span>
            <span class="legend-label">Acceptable</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot rank-poor"></span>
            <span class="legend-label">Needs Improvement</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { getUserInfo } from '@/services/cookieService';

export default {
  name: 'PredictionAccuracyWidget',
  props: {
    actualLoad: {
      type: Array,
      default: () => []
    },
    predictedLoad: {
      type: Array,
      default: () => []
    },
    actualPv: {
      type: Array,
      default: () => []
    },
    predictedPv: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showAdvanced: false,
      showFormula: false
    };
  },
  computed: {
    accessLevelNumber() {
      const userInfo = getUserInfo();
      const accessLevel = userInfo?.accessLevel;
      return accessLevel ? (typeof accessLevel === 'string' ? parseInt(accessLevel, 10) : accessLevel) : 0;
    },
    hasData() {
      // Only show widget if there are valid pairs (both actual and predicted values exist)
      return (this.loadValidPairs.length > 0) || (this.pvValidPairs.length > 0);
    },
    // Get valid pairs where both actual and predicted values exist
    loadValidPairs() {
      const pairs = [];
      const minLength = Math.min(this.actualLoad.length, this.predictedLoad.length);
      for (let i = 0; i < minLength; i++) {
        const a = parseFloat(this.actualLoad[i]);
        const p = parseFloat(this.predictedLoad[i]);
        if (!isNaN(a) && !isNaN(p) && a != null && p != null) {
          pairs.push({ actual: a, predicted: p });
        }
      }
      return pairs;
    },
    pvValidPairs() {
      const pairs = [];
      const minLength = Math.min(this.actualPv.length, this.predictedPv.length);
      for (let i = 0; i < minLength; i++) {
        const a = parseFloat(this.actualPv[i]);
        const p = parseFloat(this.predictedPv[i]);
        if (!isNaN(a) && !isNaN(p) && a != null && p != null) {
          pairs.push({ actual: a, predicted: p });
        }
      }
      return pairs;
    },
    // Calculate total energy (kWh) - only for valid pairs where both values exist
    loadActualTotal() {
      const pairs = this.loadValidPairs;
      if (pairs.length === 0) return 0;
      const intervalHours = 5 / 60; // 5 minutes = 5/60 hours
      return pairs.reduce((sum, pair) => sum + (pair.actual * intervalHours), 0);
    },
    loadPredictedTotal() {
      const pairs = this.loadValidPairs;
      if (pairs.length === 0) return 0;
      const intervalHours = 5 / 60;
      return pairs.reduce((sum, pair) => sum + (pair.predicted * intervalHours), 0);
    },
    pvActualTotal() {
      const pairs = this.pvValidPairs;
      if (pairs.length === 0) return 0;
      const intervalHours = 5 / 60;
      return pairs.reduce((sum, pair) => sum + (pair.actual * intervalHours), 0);
    },
    pvPredictedTotal() {
      const pairs = this.pvValidPairs;
      if (pairs.length === 0) return 0;
      const intervalHours = 5 / 60;
      return pairs.reduce((sum, pair) => sum + (pair.predicted * intervalHours), 0);
    },
    loadAccuracy() {
      if (this.loadActualTotal === 0 && this.loadPredictedTotal === 0) return 100;
      if (this.loadActualTotal === 0 || this.loadPredictedTotal === 0) return 0;
      // Use symmetric formula: accuracy based on max of actual and predicted
      const maxValue = Math.max(this.loadActualTotal, this.loadPredictedTotal);
      const accuracy = (1 - Math.abs(this.loadPredictedTotal - this.loadActualTotal) / maxValue) * 100;
      return Math.max(0, Math.min(100, accuracy));
    },
    pvAccuracy() {
      if (this.pvActualTotal === 0 && this.pvPredictedTotal === 0) return 100;
      if (this.pvActualTotal === 0 || this.pvPredictedTotal === 0) return 0;
      // Use symmetric formula: accuracy based on max of actual and predicted
      const maxValue = Math.max(this.pvActualTotal, this.pvPredictedTotal);
      const accuracy = (1 - Math.abs(this.pvPredictedTotal - this.pvActualTotal) / maxValue) * 100;
      return Math.max(0, Math.min(100, accuracy));
    },
    loadMetrics() {
      return this.calculateMetrics(this.actualLoad, this.predictedLoad);
    },
    pvMetrics() {
      return this.calculateMetrics(this.actualPv, this.predictedPv);
    }
  },
  methods: {
    formatKWh(value) {
      return parseFloat(value).toFixed(2);
    },
    formatPercent(value) {
      return parseFloat(value).toFixed(2) + '%';
    },
    formatMetric(value) {
      if (value === null || value === undefined || isNaN(value)) return 'N/A';
      return parseFloat(value).toFixed(4);
    },
    getAccuracyClass(accuracy) {
      if (accuracy >= 90) return 'excellent';
      if (accuracy >= 80) return 'good';
      if (accuracy >= 70) return 'fair';
      return 'poor';
    },
    getAccuracyColor(accuracy) {
      if (accuracy >= 90) return '#4CAF50'; // Green
      if (accuracy >= 80) return '#8BC34A'; // Light green
      if (accuracy >= 70) return '#FFC107'; // Amber
      return '#F44336'; // Red
    },
    getRankClass(rank) {
      if (!rank) return '';
      const lowerRank = rank.toLowerCase();
      if (lowerRank === 'excellent') return 'rank-excellent';
      if (lowerRank === 'good') return 'rank-good';
      if (lowerRank === 'fair') return 'rank-fair';
      if (lowerRank === 'acceptable') return 'rank-acceptable';
      return 'rank-poor';
    },
    calculateMetrics(actual, predicted) {
      if (!actual || !predicted || actual.length === 0 || predicted.length === 0) {
        return this.getEmptyMetrics();
      }

      // Filter out null/undefined/NaN values and ensure arrays are same length
      // Only include pairs where both actual and predicted values are valid
      const pairs = [];
      const minLength = Math.min(actual.length, predicted.length);
      for (let i = 0; i < minLength; i++) {
        const a = parseFloat(actual[i]);
        const p = parseFloat(predicted[i]);
        // Only include if both values are valid numbers (not NaN, null, or undefined)
        if (!isNaN(a) && !isNaN(p) && a != null && p != null && actual[i] != null && predicted[i] != null) {
          pairs.push({ actual: a, predicted: p });
        }
      }

      if (pairs.length === 0) {
        return this.getEmptyMetrics();
      }

      const n = pairs.length;
      
      // Calculate residuals
      const residuals = pairs.map(p => p.actual - p.predicted);
      
      // MSE (Mean Squared Error)
      const mse = residuals.reduce((sum, r) => sum + (r * r), 0) / n;
      
      // MAE (Mean Absolute Error)
      const mae = residuals.reduce((sum, r) => sum + Math.abs(r), 0) / n;
      
      // MAPE (Mean Absolute Percentage Error)
      const mape = pairs.reduce((sum, p) => {
        if (p.actual !== 0) {
          return sum + Math.abs((p.actual - p.predicted) / p.actual);
        }
        return sum;
      }, 0) / n * 100;
      
      // RMSE (Root Mean Squared Error)
      const rmse = Math.sqrt(mse);
      
      // Mean of actual values
      const meanActual = pairs.reduce((sum, p) => sum + p.actual, 0) / n;
      
      // NRMSE (Normalized RMSE)
      const nrmse = meanActual !== 0 ? rmse / meanActual : 0;
      
      // Standard Deviation of Residuals (Sigma)
      const meanResidual = residuals.reduce((sum, r) => sum + r, 0) / n;
      const variance = residuals.reduce((sum, r) => sum + Math.pow(r - meanResidual, 2), 0) / n;
      const sigma = Math.sqrt(variance);
      
      // CE (Coefficient of Efficiency) - Nash-Sutcliffe Efficiency
      const varianceActual = pairs.reduce((sum, p) => sum + Math.pow(p.actual - meanActual, 2), 0) / n;
      const ce = varianceActual !== 0 ? 1 - (mse / varianceActual) : 0;
      
      // CRPS (Continuous Ranked Probability Score) - simplified version
      // For deterministic predictions, CRPS reduces to MAE
      const crps = mae;
      
      return {
        mse,
        mae,
        maeRank: this.getMaeRank(mae, meanActual),
        mape,
        mapeRank: this.getMapeRank(mape),
        rmse,
        rmseRank: this.getRmseRank(rmse, meanActual),
        nrmse,
        nrmseRank: this.getNrmseRank(nrmse),
        sigma,
        sigmaRank: this.getSigmaRank(sigma, meanActual),
        ce,
        ceRank: this.getCeRank(ce),
        crps,
        crpsRank: this.getCrpsRank(crps, meanActual),
        mseRank: this.getMseRank(mse, meanActual)
      };
    },
    getEmptyMetrics() {
      return {
        mse: null,
        mseRank: null,
        mae: null,
        maeRank: null,
        mape: null,
        mapeRank: null,
        rmse: null,
        rmseRank: null,
        nrmse: null,
        nrmseRank: null,
        sigma: null,
        sigmaRank: null,
        ce: null,
        ceRank: null,
        crps: null,
        crpsRank: null
      };
    },
    // UPDATED: Slightly relaxed thresholds for energy predictions (load is volatile but more predictable than prices)
    getMapeRank(mape) {
      if (mape < 8) return 'Excellent';
      if (mape < 18) return 'Good';
      if (mape < 30) return 'Fair';
      if (mape < 45) return 'Acceptable';
      return 'Needs Improvement';
    },
    // UPDATED: Relaxed NRMSE thresholds
    getNrmseRank(nrmse) {
      if (nrmse < 0.08) return 'Excellent';
      if (nrmse < 0.18) return 'Good';
      if (nrmse < 0.30) return 'Fair';
      if (nrmse < 0.45) return 'Acceptable';
      return 'Needs Improvement';
    },
    // UPDATED: Adjusted CE thresholds
    getCeRank(ce) {
      if (ce > 0.88) return 'Excellent';
      if (ce > 0.68) return 'Good';
      if (ce > 0.45) return 'Fair';
      if (ce > 0.25) return 'Acceptable';
      return 'Needs Improvement';
    },
    // UPDATED: Relaxed relative MSE thresholds
    getMseRank(mse, meanActual) {
      if (meanActual === 0) return null;
      const relativeMse = Math.sqrt(mse) / meanActual;
      if (relativeMse < 0.08) return 'Excellent';
      if (relativeMse < 0.18) return 'Good';
      if (relativeMse < 0.30) return 'Fair';
      if (relativeMse < 0.45) return 'Acceptable';
      return 'Needs Improvement';
    },
    // UPDATED: Relaxed relative MAE thresholds
    getMaeRank(mae, meanActual) {
      if (meanActual === 0) return null;
      const relativeMae = mae / meanActual;
      if (relativeMae < 0.08) return 'Excellent';
      if (relativeMae < 0.18) return 'Good';
      if (relativeMae < 0.30) return 'Fair';
      if (relativeMae < 0.45) return 'Acceptable';
      return 'Needs Improvement';
    },
    // UPDATED: Relaxed relative RMSE thresholds
    getRmseRank(rmse, meanActual) {
      if (meanActual === 0) return null;
      const relativeRmse = rmse / meanActual;
      if (relativeRmse < 0.08) return 'Excellent';
      if (relativeRmse < 0.18) return 'Good';
      if (relativeRmse < 0.30) return 'Fair';
      if (relativeRmse < 0.45) return 'Acceptable';
      return 'Needs Improvement';
    },
    // UPDATED: Relaxed relative Sigma thresholds
    getSigmaRank(sigma, meanActual) {
      if (meanActual === 0) return null;
      const relativeSigma = sigma / meanActual;
      if (relativeSigma < 0.08) return 'Excellent';
      if (relativeSigma < 0.18) return 'Good';
      if (relativeSigma < 0.30) return 'Fair';
      if (relativeSigma < 0.45) return 'Acceptable';
      return 'Needs Improvement';
    },
    // UPDATED: Relaxed relative CRPS thresholds
    getCrpsRank(crps, meanActual) {
      if (meanActual === 0) return null;
      const relativeCrps = crps / meanActual;
      if (relativeCrps < 0.08) return 'Excellent';
      if (relativeCrps < 0.18) return 'Good';
      if (relativeCrps < 0.30) return 'Fair';
      if (relativeCrps < 0.45) return 'Acceptable';
      return 'Needs Improvement';
    },
    renderLatex(latexString, displayMode = false) {
      try {
        return katex.renderToString(latexString, {
          throwOnError: false,
          displayMode: displayMode
        });
      } catch (error) {
        console.error('Error rendering LaTeX:', error);
        return latexString;
      }
    }
  }
};
</script>

<style scoped>
.prediction-accuracy-widget {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  padding: 1.25rem;
  margin: 1.5rem 0;
}

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid var(--border-color);
}

.widget-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.toggle-advanced-btn {
  padding: 6px 14px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--card-bg);
  color: var(--text-secondary);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.toggle-advanced-btn:hover {
  border-color: var(--custom-color1, #4CAF50);
  color: var(--custom-color1, #4CAF50);
  background: rgba(76, 175, 80, 0.05);
}

.toggle-advanced-btn.active {
  background: var(--custom-color1, #4CAF50);
  color: white;
  border-color: var(--custom-color1, #4CAF50);
}

.no-data-message {
  text-align: center;
  padding: 2rem;
  color: var(--text-tertiary);
  font-style: italic;
}

.basic-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.metric-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 1rem;
  transition: all 0.3s ease;
}

.metric-card:hover {
  box-shadow: 0 4px 12px var(--shadow-color);
  transform: translateY(-2px);
}

.metric-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.metric-icon {
  font-size: 1.25rem;
}

.metric-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--text-primary);
}

.metric-comparison {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.comparison-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
}

.comparison-row .label {
  color: var(--text-tertiary);
  font-weight: 500;
}

.comparison-row .value {
  color: var(--text-primary);
  font-weight: 600;
}

.accuracy-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-color);
}

.accuracy-row .label {
  color: var(--text-tertiary);
  font-weight: 500;
  font-size: 0.9rem;
  min-width: 70px;
}

.accuracy-value {
  font-weight: 700;
  font-size: 1rem;
  min-width: 70px;
}

.accuracy-value.excellent {
  color: #4CAF50;
}

.accuracy-value.good {
  color: #8BC34A;
}

.accuracy-value.fair {
  color: #FFC107;
}

.accuracy-value.poor {
  color: #F44336;
}

.accuracy-bar-container {
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: var(--bg-tertiary);
  position: relative;
  overflow: hidden;
}

.accuracy-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s ease;
}

.advanced-metrics {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px solid var(--border-color);
}

.advanced-section {
  margin-bottom: 1.5rem;
}

.advanced-section:last-child {
  margin-bottom: 0;
}

.advanced-title {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
}

.stat-metric {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.stat-label {
  font-size: 0.75rem;
  color: var(--text-tertiary);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.stat-rank {
  font-size: 0.75rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
}

/* Rank Legend */
.rank-legend {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--bg-secondary);
  border-radius: 8px;
  margin-top: 1.5rem;
  flex-wrap: wrap;
}

.legend-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-tertiary);
}

.legend-items {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.legend-label {
  font-size: 0.75rem;
  color: var(--text-tertiary);
  white-space: nowrap;
}

.legend-dot.rank-excellent {
  background: #2E7D32;
}

.legend-dot.rank-good {
  background: #4CAF50;
}

.legend-dot.rank-fair {
  background: #FF9800;
}

.legend-dot.rank-acceptable {
  background: #FFB74D;
}

.legend-dot.rank-poor {
  background: #F44336;
}

/* Rank Dots in Metrics */
.stat-rank-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  margin-left: 0.5rem;
  flex-shrink: 0;
  vertical-align: middle;
}

.stat-rank-dot.rank-excellent {
  background: #2E7D32;
}

.stat-rank-dot.rank-good {
  background: #4CAF50;
}

.stat-rank-dot.rank-fair {
  background: #FF9800;
}

.stat-rank-dot.rank-acceptable {
  background: #FFB74D;
}

.stat-rank-dot.rank-poor {
  background: #F44336;
}

/* Formula Section */
.formula-section {
  margin: 1rem 0;
  border-top: 1px solid #e0e0e0;
  padding-top: 1rem;
}

.formula-toggle-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: none;
  color: var(--text-tertiary);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 0;
  transition: color 0.2s ease;
}

.formula-toggle-btn:hover {
  color: var(--custom-color1, #4CAF50);
}

.toggle-icon {
  font-size: 0.75rem;
  transition: transform 0.2s ease;
}

.formula-content {
  margin-top: 1rem;
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.formula-box {
  background: var(--card-bg);
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  margin-bottom: 0.75rem;
}

.formula-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.formula-expression {
  font-size: 1rem;
  color: var(--text-primary);
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 6px;
  overflow-x: auto;
  text-align: center;
}

.formula-expression .katex {
  font-size: 1.1rem;
}

.formula-description {
  font-size: 0.85rem;
  line-height: 1.6;
  color: #666;
  margin: 0;
}

/* Formula expand animation */
.formula-expand-enter-active,
.formula-expand-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.formula-expand-enter-from,
.formula-expand-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
}

@media (max-width: 750px) {
  .basic-metrics {
    grid-template-columns: 1fr;
  }
  
  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .widget-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
}
</style>